import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';

// Types de pages disponibles dans l'application
// Note: 'company-selection' is intentionally not in PAGES as it's a special admin-only page
// that should not appear in normal navigation or be restored from localStorage
export type PageType = 'chat' | 'analyse-doc' | 'page3' | 'page4' | 'enrichir' | 'notifications' | 'company-selection';

// Navigable pages that appear in the sidebar - excludes special pages like 'company-selection'
export const NAVIGABLE_PAGE_IDS: PageType[] = ['chat', 'analyse-doc', 'page3', 'page4', 'enrichir', 'notifications'];

export interface PageInfo {
  id: PageType;
  name: string;
  icon: string;
}

export const PAGES: PageInfo[] = [
  { id: 'chat', name: 'Le Chat', icon: 'chat' },
  { id: 'analyse-doc', name: 'Analyse de doc', icon: 'analyse-doc' },
  { id: 'page3', name: 'Mes Clauses', icon: 'page3' },
  { id: 'page4', name: 'Mes Contrats', icon: 'page4' },
  { id: 'enrichir', name: 'Enrichir', icon: 'enrichir' },
  { id: 'notifications', name: 'Notifications', icon: 'notifications' },
];

export interface AppContextType {
  // Text injection to chatbot
  addTextToChatbot: (text: string) => void;
  textToAddToChatbot: string;
  // Navigation entre pages
  activePage: PageType;
  setActivePage: (page: PageType) => void;
  initialContractId: number | null;
  setInitialContractId: (id: number | null) => void;
  initialClauseId: number | null;
  setInitialClauseId: (id: number | null) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const ACTIVE_PAGE_STORAGE_KEY = 'corpus_active_page';

const getInitialPage = (): PageType => {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return 'chat';
  }
  const stored = localStorage.getItem(ACTIVE_PAGE_STORAGE_KEY);
  // Only restore navigable pages - special pages like 'company-selection' should not persist
  if (stored && NAVIGABLE_PAGE_IDS.includes(stored as PageType)) {
    return stored as PageType;
  }
  return 'chat';
};

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [textToAddToChatbot, setTextToAddToChatbot] = useState<string>('');
  const [activePage, setActivePageState] = useState<PageType>(getInitialPage);
  const [initialContractId, setInitialContractId] = useState<number | null>(null);
  const [initialClauseId, setInitialClauseId] = useState<number | null>(null);

  // Persist activePage to localStorage
  const setActivePage = React.useCallback((page: PageType) => {
    localStorage.setItem(ACTIVE_PAGE_STORAGE_KEY, page);
    setActivePageState(page);
  }, []);

  // Add text to chatbot
  const addTextToChatbot = React.useCallback((text: string) => {
    setTextToAddToChatbot(text);
  }, []);

  // Clear text after use
  useEffect(() => {
    if (textToAddToChatbot) {
      const timeoutId = setTimeout(() => {
        setTextToAddToChatbot('');
      }, 200);
      return () => clearTimeout(timeoutId);
    }
  }, [textToAddToChatbot]);

  return (
    <AppContext.Provider value={{
      addTextToChatbot,
      textToAddToChatbot,
      activePage,
      setActivePage,
      initialContractId,
      setInitialContractId,
      initialClauseId,
      setInitialClauseId
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};
